import React, { useEffect, useState } from "react";
import { useAuth } from '../../context/AuthContext';
import { StandardPageSkeleton } from '../../components/common/Skeleton';
import { PageFadeSection } from '../../components/common/PageFadeSection';
import api from '../../api/axios';
import { 
  Briefcase, 
  Plus, 
  Search, 
  Filter, 
  RefreshCw,
  CheckCircle,
  AlertTriangle,
  Clock,
  User,
  DollarSign,
  ChevronDown,
  MapPin
} from 'lucide-react';

const ServiceJobsPage = () => {
  const { getAccessToken } = useAuth();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');

  useEffect(() => {
    fetchJobs();
  }, []);

  const fetchJobs = async () => {
    try {
      const token = await getAccessToken();
      const response = await api.get('/api/servicejob', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setJobs(response.data);
    } catch (error) {
      console.error('Failed to fetch jobs:', error);
      // Demo data
      setJobs([
        { id: '1', serviceOrderId: 'SO-001', customerName: 'Acme Corp', laborDuration: '4h', isFinalConfirmation: true, syncStatus: 'synced' },
        { id: '2', serviceOrderId: 'SO-002', customerName: 'Tech Industries', laborDuration: '2.5h', isFinalConfirmation: false, syncStatus: 'synced' },
        { id: '3', serviceOrderId: 'SO-003', customerName: 'Global Solutions', laborDuration: '6h', isFinalConfirmation: false, syncStatus: 'pending' },
        { id: '4', serviceOrderId: 'SO-004', customerName: 'Local Business', laborDuration: '1h', isFinalConfirmation: true, syncStatus: 'synced' },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (isFinal) => {
    if (isFinal) {
      return (
        <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium border bg-green-100 text-green-700 border-green-200 dark:bg-green-900/30 dark:text-green-400 dark:border-green-800">
          <CheckCircle className="w-3 h-3" />
          Final
        </span>
      );
    }
    return (
        <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium border bg-yellow-100 text-yellow-700 border-yellow-200 dark:bg-yellow-900/30 dark:text-yellow-400 dark:border-yellow-800">
        <Clock className="w-3 h-3" />
        Pending
      </span>
    );
  };

  const filteredJobs = jobs.filter(job => {
    const matchesSearch = job.customerName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         job.serviceOrderId?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterStatus === 'all' || 
                         (filterStatus === 'final' && job.isFinalConfirmation) ||
                         (filterStatus === 'pending' && !job.isFinalConfirmation);
    return matchesSearch && matchesFilter;
  });

  if (loading) {
    return <StandardPageSkeleton />;
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <PageFadeSection>
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Service Jobs</h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1">Manage service confirmations and labor</p>
        </div>
        <div className="flex items-center gap-3">
          <button className="flex items-center gap-2 px-4 py-2.5 bg-operis-blue hover:bg-primary-700 text-white rounded-xl font-medium transition-all">
            <RefreshCw className="w-5 h-5" />
            Sync to SAP
          </button>
          <button className="flex items-center gap-2 px-4 py-2.5 bg-green-600 hover:bg-green-700 text-white rounded-xl font-medium transition-all">
            <Plus className="w-5 h-5" />
            New Job
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-100 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-orange-100 dark:bg-orange-900/30 rounded-lg">
              <Briefcase className="w-5 h-5 text-orange-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{jobs.length}</p>
              <p className="text-sm text-gray-500 dark:text-gray-400">Total Jobs</p>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-100 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
              <CheckCircle className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{jobs.filter(j => j.isFinalConfirmation).length}</p>
              <p className="text-sm text-gray-500 dark:text-gray-400">Confirmed</p>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-100 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-yellow-100 dark:bg-yellow-900/30 rounded-lg">
              <Clock className="w-5 h-5 text-yellow-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{jobs.filter(j => !j.isFinalConfirmation).length}</p>
              <p className="text-sm text-gray-500 dark:text-gray-400">Pending</p>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-100 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
              <User className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{new Set(jobs.map(j => j.customerName)).size}</p>
              <p className="text-sm text-gray-500 dark:text-gray-400">Customers</p>
            </div>
          </div>
        </div>
      </div>
      </PageFadeSection>

      <PageFadeSection>
      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl p-4 shadow-sm border border-gray-100 dark:border-gray-700">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search jobs..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 border border-gray-200 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-operis-blue focus:border-transparent dark:bg-gray-700 dark:text-white"
            />
          </div>
          <div className="relative">
            <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="pl-10 pr-10 py-2.5 border border-gray-200 dark:border-gray-600 rounded-xl appearance-none bg-white dark:bg-gray-700 dark:text-white cursor-pointer"
            >
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="final">Confirmed</option>
            </select>
            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
          </div>
        </div>
      </div>

      {/* Jobs Table */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-700 border-b border-gray-100 dark:border-gray-700">
              <tr>
                <th className="text-left px-6 py-4 text-sm font-semibold text-gray-600 dark:text-gray-400">Order ID</th>
                <th className="text-left px-6 py-4 text-sm font-semibold text-gray-600 dark:text-gray-400">Customer</th>
                <th className="text-left px-6 py-4 text-sm font-semibold text-gray-600 dark:text-gray-400">Duration</th>
                <th className="text-left px-6 py-4 text-sm font-semibold text-gray-600 dark:text-gray-400">Status</th>
                <th className="text-left px-6 py-4 text-sm font-semibold text-gray-600 dark:text-gray-400">Sync</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {filteredJobs.map((job) => (
                <tr key={job.id} className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-orange-50 dark:bg-orange-900/20 rounded-lg">
                        <Briefcase className="w-4 h-4 text-orange-600" />
                      </div>
                      <span className="font-medium text-gray-900 dark:text-white">{job.serviceOrderId}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <User className="w-4 h-4 text-gray-400" />
                      <span className="text-gray-600 dark:text-gray-400">{job.customerName}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className="font-medium text-gray-900 dark:text-white">{job.laborDuration}</span>
                  </td>
                  <td className="px-6 py-4">{getStatusBadge(job.isFinalConfirmation)}</td>
                  <td className="px-6 py-4">
                    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${
                      job.syncStatus === 'synced' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' : 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'
                    }`}>
                      {job.syncStatus === 'synced' ? <CheckCircle className="w-3 h-3" /> : <Clock className="w-3 h-3" />}
                      {job.syncStatus}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      </PageFadeSection>
    </div>
  );
};

export default ServiceJobsPage;
